/**
 * Frontend-Only Types
 *
 * This file contains types and enums that are frontend-specific and not (yet) in api-contracts.ts
 *
 * NOTE: LlmVendor is requested to be added to backend api-contracts.ts (see MISSING_BACKEND_ENUMS.md)
 * Once backend adds it, this should be removed and imported from @cabal/shared/api-client/contracts instead
 */

/**
 * Re-export Provider from backend for convenience
 * Backend v1.3.0 added this enum (was requested as LlmVendor)
 */
import { Provider } from "@cabal/shared/domain/llm/model/enums.js";
import { TeamCategory } from "@cabal/shared/domain/session/team/enums.js";
import { SessionStatus } from "@cabal/shared/domain/session/enums.js";
export { Provider } from "@cabal/shared/domain/llm/model/enums.js";
/** @deprecated Use `Provider` from canonical. Kept as alias during cutover. */
export type LlmVendorLocal = Provider;

/**
 * Frontend UI-specific team member representation
 * Transforms MemberInfo from backend with UI-specific fields. Distinct from
 * the canonical `Member` entity — this is the projection the UI consumes.
 */
export interface TeamMemberLocal {
  name: string;
  role: string;
  llmVendor: string; // Provider identifier (e.g., 'anthropic', 'openai')
  colorHex: string; // From MemberInfo.color or generated fallback
  llmModel: string;
  isThinking: boolean; // Derived from activity.is_active
}

// SessionStatus moved to canonical: import from `@cabal/shared/domain/session/enums.js`.
// FE no longer carries an `Errored` member — error is signaled by Halted + haltReason === HaltReason.Error.
export { SessionStatus };

// ============================================================================
// SESSION CONTEXT TYPES
// ============================================================================

/**
 * Stalling phase enum for session initialization
 * Controls the visual progression during session setup
 */
export enum StallingPhase {
  BuildingIdentities = "buildingIdentities",
  Characterising = "characterising",
  Augmenting = "augmenting",
  Missioning = "missioning",
  Deploying = "deploying",
}

/**
 * Temper enum for visual mood/tone indication
 * Used to color UI elements based on sentiment
 */
export enum Temper {
  Good = "good",
  Neutral = "neutral",
  Bad = "bad",
  Warn = "warn",
}

type TemperColorTypeKey = "Accent" | "White" | "Danger" | "Warn";

const TEMPER_COLOR_KEY_BY_TEMPER: Record<Temper, TemperColorTypeKey> = {
  [Temper.Good]: "Accent",
  [Temper.Neutral]: "White",
  [Temper.Bad]: "Danger",
  [Temper.Warn]: "Warn",
};

/**
 * Alias Temper to MessageModalTemper for compatibility
 */
export { Temper as MessageModalTemper };

/**
 * Maps Temper to the corresponding ColorType
 * @param temper - The temper value
 * @returns The corresponding ColorType string
 */
export function getTemperColor(temper: Temper): TemperColorTypeKey {
  return TEMPER_COLOR_KEY_BY_TEMPER[temper];
}

/**
 * Writing chunks enum for animation granularity
 * Controls how text is revealed in typewriter effects
 */
export enum WritingChunks {
  Letter = "letter",
  Word = "word",
  Line = "line",
}

/**
 * Text alignment enum
 * Controls horizontal text alignment
 */
export enum TextAlign {
  Left = "left",
  Center = "center",
}

/**
 * Chat mode enum for chat panel
 */
export enum ChatMode {
  Main = "main",
  Product = "product",
}

/**
 * Display mode for sessions list (Active vs Archived)
 */
export enum SessionsDisplayMode {
  Active = "active",
  Archived = "archived",
}

/**
 * Diff segment type for content comparison
 * Used in diff modal to highlight added/removed text
 * Future-proof: can be expanded with more types (e.g., Modified, Moved)
 */
export enum DiffSegmentType {
  Plain = "plain",
  Added = "added",
  Removed = "removed",
}

/**
 * Diff segment for content comparison
 * Each segment represents a portion of text with a specific diff type
 */
export interface DiffSegment {
  content: string;
  type: DiffSegmentType;
}

/**
 * Navigation state for product stack/breadcrumbs
 */
export interface NavigationState {
  productStack: string[]; // Stack of product IDs for breadcrumb navigation
  focusedProductId: string | null; // Currently focused product
  selectedVersionId: string | null; // Currently selected version for viewing
}

/**
 * Chat state for chat panel
 */
export interface ChatState {
  mode: ChatMode; // Chat mode toggle
  selectedProductId: string | null; // Selected product for product chat
  targetVersionId: string | null; // Pending version target for deterministic chat scroll
  targetMessageId: string | null; // Pending message target for deterministic chat scroll
  // A request identity, not another target id; chatHasPendingReferenceTarget
  // intentionally continues to read only the two nullable target fields.
  targetRequestId: number;
  indicatedMessageId: string | null; // Purely presentational message emphasis
  indicationRequestId: number; // Replays emphasis when the same message is indicated again
}

/**
 * True iff the chat has a queued reference-navigation target the user has not
 * yet seen. The auto-reopen effect on mobile uses this to surface the chat
 * panel after a Creference click closes it.
 *
 * INVARIANT: when adding a new `target*Id` field to `ChatState`, extend this
 * predicate. Anything that opens the chat on a pending target reads from here,
 * so new ref kinds become visible automatically — no mode-specific gates.
 */
export function chatHasPendingReferenceTarget(
  chat: Pick<ChatState, "targetVersionId" | "targetMessageId">,
): boolean {
  return chat.targetVersionId !== null || chat.targetMessageId !== null;
}

/**
 * Unread tracking state
 */
export interface UnreadState {
  collabs: Record<string, number>; // productId -> unread count
  versions: Record<string, number>; // productId -> unseen count
  products: Record<string, number>; // productId -> unseen subproduct count
  collabsByProduct: Record<string, string[]>; // productId -> collab IDs
  versionsByProduct: Record<string, string[]>; // productId -> version IDs
}

export const MAIN_CHAT_PRODUCT_KEY = "MAIN_CHAT";

export type ProductReadScopeKey = string;

export interface ProductReadMarker {
  productSeenAt: Date | null;
  collabsReadAt: Date | null;
}

export interface ProductReadMarkerMap {
  [scopeKey: ProductReadScopeKey]: ProductReadMarker;
}

export interface ProductUnreadCollabCounts {
  [scopeKey: ProductReadScopeKey]: {
    totalCollabs: number;
    unreadCollabs: number;
  };
}

export interface ProductSeenState {
  lastChangedProduct: Date | null;
  lastAddedCollabs: Date | null;
  lastAddedVersion: Date | null;
}

export interface ProductsSeenMap {
  [productId: string]: ProductSeenState;
}

/**
 * Polling timestamps for delta queries
 */
export interface PollTimestamps {
  productsSince: string | null;
  chatSince: string | null;
  activitySince: string | null;
  productReadsSince: string | null;
  collabsSinceByProduct: Record<string, string | null>;
}

/**
 * Loading states for various data
 */
export interface LoadingStates {
  session: boolean;
  products: boolean;
  members: boolean;
  chat: boolean;
}

/**
 * Tracked product fields for change detection
 * When a product update arrives, we detect which fields changed
 */
export const TRACKED_PRODUCT_FIELD = {
  Name: "name",
  Type: "type",
  Role: "role",
  Status: "status",
  Owner: "owner",
  CollabCount: "collabCount",
  VersionCount: "versionCount",
  SubproductCount: "subproductCount",
  LatestVersionNumber: "latestVersionNumber",
  VersionIds: "versionIds",
  AttachedFiles: "attachedFiles",
  Description: "description",
  DefinitionOfDone: "definitionOfDone",
  Summary: "summary",
} as const;
export type TrackedProductField =
  (typeof TRACKED_PRODUCT_FIELD)[keyof typeof TRACKED_PRODUCT_FIELD];

/**
 * Product changes tracking state
 * Maps product ID to set of changed fields
 */
export type ProductChanges = Record<string, Set<TrackedProductField>>;

/**
 * Resource kinds that can carry a live-flash one-shot animation when they
 * mutate during the user's session.
 */
export enum LiveFlashKind {
  Product = "product",
  Version = "version",
  Collab = "collab",
  File = "file",
  Reflection = "reflection",
  Member = "member",
  Session = "session",
}

/**
 * Live flash auto-clear duration. A flagged entity's flash entry is removed
 * after this many milliseconds so the UI returns to its rest state.
 */
export const LIVE_FLASH_MS = 2000;

/**
 * Buffer added to an own-collab's createdAt when advancing the local read
 * marker for that scope, so a sibling collab arriving in the same logical
 * batch but with slight server-side clock skew is also covered.
 * Independent of the live-flash duration — both happen to be 2 s today
 * but tuning either should not move the other.
 */
export const OWN_COLLAB_READ_ADVANCE_BUFFER_MS = 2000;

/**
 * Kinds of activity hints that can be pushed to a member chip when the
 * member authors a new resource observed via polling.
 */
export enum MemberActivityKind {
  Collab = "collab",
  Reflection = "reflection",
  Version = "version",
}

/**
 * Auto-clear duration for a member-activity hint overlay. Slightly longer
 * than LIVE_FLASH_MS so the user has time to read the excerpt.
 */
export const MEMBER_ACTIVITY_MS = 3000;

export interface MemberActivityHint {
  kind: MemberActivityKind;
  excerpt: string;
  pushedAt: number;
}

export type MemberActivityMap = Record<string, MemberActivityHint>;

export interface LiveFlashEntry {
  fields: Set<string>;
  flashedAt: number;
}

export type LiveFlashesByKind = Record<string, LiveFlashEntry>;

export type LiveFlashes = Record<LiveFlashKind, LiveFlashesByKind>;

export function emptyLiveFlashes(): LiveFlashes {
  return {
    [LiveFlashKind.Product]: {},
    [LiveFlashKind.Version]: {},
    [LiveFlashKind.Collab]: {},
    [LiveFlashKind.File]: {},
    [LiveFlashKind.Reflection]: {},
    [LiveFlashKind.Member]: {},
    [LiveFlashKind.Session]: {},
  };
}

/**
 * Generic live-flash field used by non-product kinds when the only signal
 * is "this resource just arrived in a poll cycle after initial-load".
 * Product flashes use TrackedProductField (richer per-field info).
 */
export enum LiveFlashCommonField {
  Arrival = "arrival",
}

/**
 * Fields tracked for Member live-flash. `characteristics` is intentionally
 * omitted (decision #4 in the live-state plan — too noisy).
 */
export enum TrackedMemberField {
  Name = "name",
  Role = "role",
}

/**
 * Per-kind input to `actions.flagLiveFlash`. Product callers must pass
 * `TrackedProductField` strings; other kinds remain `string[]` until their
 * field enums land with Phases 2–9.
 */
export type FlagLiveFlashInput =
  | {
      kind: LiveFlashKind.Product;
      entityId: string;
      fields: readonly TrackedProductField[];
      scopeProductId?: string;
    }
  | {
      kind: Exclude<LiveFlashKind, LiveFlashKind.Product>;
      entityId: string;
      fields: readonly string[];
      scopeProductId?: string;
    };

// ============================================================================
// API CLIENT TYPES (Frontend infrastructure)
// ============================================================================

/**
 * Standard response envelope for paginated/delta endpoints
 * v1.1.0 feature - used by apiClient
 * Note: Backend doesn't export this, but uses this structure
 */
export interface ResponseEnvelope<T> {
  now: string; // Server timestamp for next delta query
  items: T[]; // The actual data
  next_cursor?: string; // For pagination
}

/**
 * Standard error response structure
 * Used by apiClient error handling.
 *
 * `userMessage` is the curated, user-safe sentence — present only when the BE
 * has a clean explanation. Absent ⇒ FE falls back to a generic message.
 */
export interface ErrorResponse {
  statusCode: number;
  error: string;
  message?: string;
  code?: string;
  userMessage?: string;
  details?: {
    issues?: ApiValidationIssue[];
  };
}

export interface ApiValidationIssue {
  field?: string;
  path?: Array<string | number>;
  message?: string;
  code?: string;
}

/**
 * Type alias for backward compatibility
 * Some code may still reference SessionAccess
 */
export type SessionAccess =
  import("@cabal/shared/domain/session/enums.js").Access;

// ============================================================================
// MESSAGE TYPE GUARDS
// ============================================================================

import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
/**
 * Check if a collab is a session-level collab (chat-like, not on a product)
 */
export function isSessionCollab(msg: Collab): boolean {
  return msg.onProduct === null;
}

/**
 * Check if a collab is on a product
 */
export function isProductCollab(msg: Collab): boolean {
  return msg.onProduct !== null;
}
